import { Space, Table, Typography } from 'antd';
import { EmptyTableIcon } from 'assets/icons';
import BaseTable from 'components/Table';
import withGuard from 'components/withGuard';
import { AdminContext } from 'constants/contexts';
import { PERMISSIONS } from 'constants/permissions';
import dayjs from 'dayjs';
import React, {
  useCallback, useContext, useMemo, useRef, useState,
} from 'react';
import { Outlet } from 'react-router';
import { captureException } from 'utils/errors';
import { generateColumns } from 'utils/table';
import ReportToolbar from '../../components/ReportToolbar';
import ReportLayout from '../../layout';
import columns from './columns';
import lineItemColumns from './lineItemColumns';
import { fetchPayoutsReport } from './requests';
import './index.css';

function PayoutsReport() {
  const [filterData, setFilterData] = useState({ selected: { date: { range: null, type: 'all' } } });
  const resetFetchRef = useRef(false);
  const { organizationId } = useContext(AdminContext);

  const fetchReport = useCallback(
    (pagination) => {
      const page = resetFetchRef.current ? 1 : pagination.current;
      const queries = {
        page,
        page_size: pagination.pageSize,
        paid_at_after: filterData.selected?.date.type !== 'all' && dayjs(filterData.selected?.date.range[0])
          .format('YYYY-MM-DD'),
        paid_at_before: filterData.selected?.date.type !== 'all' && dayjs(filterData.selected?.date.range[1])
          .format('YYYY-MM-DD'),
      };
      resetFetchRef.current = false;

      return fetchPayoutsReport(queries)
        .then(({ data: { data: { results, count } } }) => ({
          data: results,
          total: count,
          page,
        }))
        .catch(captureException);
    },
    [
      organizationId,
      filterData,
    ],
  );
  const normalizedColumns = useMemo(() => generateColumns(columns), [columns]);
  const normalizedLineItemColumns = useMemo(() => generateColumns(lineItemColumns), []);
  const expandable = useMemo(() => ({
    expandedRowRender: (record) => {
      const lineItems = record.line_items || [];
      if (!lineItems.length) {
        return (
          <div className="payout-line-items">
            <Typography.Text type="secondary">
              No orders linked to this payout.
            </Typography.Text>
          </div>
        );
      }
      return (
        <div className="payout-line-items">
          <span className="payout-line-items__label">
            {lineItems.length} {lineItems.length === 1 ? 'order' : 'orders'} in this payout
          </span>
          <Table
            columns={normalizedLineItemColumns}
            dataSource={lineItems}
            rowKey={(item) => item.order_id}
            pagination={false}
            size="small"
            tableLayout="fixed"
            showHeader
            style={{ width: 700 }}
          />
        </div>
      );
    },
    rowExpandable: () => true,
  }), [normalizedLineItemColumns]);

  return (
    <ReportLayout
      title="Payouts Report"
      description="A summary of your total sales, number of orders, and payout details. Nibble sends daily or weekly payouts based on your organization's schedule."
      defaultRoute="/console/reports"
    >
      <ReportToolbar
        filterData={{ ...filterData }}
        setFilterData={setFilterData}
        hasLocationFilter={false}
        type="payouts"
        hasExport={false}
      />

      <BaseTable
        className="payouts-report-table"
        columns={normalizedColumns}
        fetchFn={fetchReport}
        expandable={expandable}
        rowClassName={() => 'payout-row'}
        empty={(
          <Space direction="vertical" className="my-10 mx-5">
            <EmptyTableIcon />
            <Typography.Paragraph>There are no records to display for the selected date.</Typography.Paragraph>
          </Space>
        )}
        scroll={{ x: 500 }}
        pagination={{
          showTotal: (total) => `Total ${total} items`,
        }}
      />

      <Outlet />
    </ReportLayout>
  );
}

export default withGuard(PayoutsReport, [PERMISSIONS.REPORTS], { redirect: true });
